import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import ModalTest from '../../BaseModal'
import * as S from '../../styles/modal-style'
import { X, ArrowLeft } from 'lucide-react'
import InfiniteSelect from '../../InfiniteSelect'
import axios from '../../../config/axios'

interface PropsInterface {
  stepId: number
  closeModal: () => void
  editingField?: { id: number, name: string, typeFieldId?: number, typeField?: { id: number, name: string } }
  onSaved?: (field?: { id: number, name: string }) => void
  onBack?: () => void
  onToggleCustomMode?: (enabled: boolean) => void
}

const AddFieldOnStep = ({ stepId, closeModal, editingField, onSaved, onBack, onToggleCustomMode }: PropsInterface) => {
  const [ fieldTypes, setFieldTypes ] = useState<{id: number, name: string}[]>([])
  const [ formData, setFormData ] = useState<{field: {id: number, name: string}, typeField: {id: number, name: string}}>({field: {id: 0, name: ''}, typeField: {id: 0, name: ''}})
  const [ fields, setFields ] = useState<{id: number, name: string, typeField: {id: number, name: string}}[]>([])
  const [ isRequired, setIsRequired ] = useState<boolean>(false)
  const [ showOnCard, setShowOnCard ] = useState<boolean>(false)
  const isEditing = !!editingField

  useEffect(() => {
    const fetchFieldTypes = async () => {
      try {
        console.log('📋 [FETCH FIELD TYPES] Buscando tipos de campo...');
        const response = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/pipeline-field/types`)
        if(response.data) {
          console.log('📋 [FETCH FIELD TYPES] Tipos retornados:', response.data.data.length);
          console.log('📋 [FETCH FIELD TYPES] Detalhes:', response.data.data.map((t: any) => ({ id: t.id, name: t.name })));
          setFieldTypes(response.data.data)
        }
      } catch (error) {
        console.error('❌ [FETCH FIELD TYPES] Erro:', error)
      }
    }

    fetchFieldTypes()
  }, [])

  useEffect(() => {
    const fetchFields = async () => {
      try {
        console.log('🔍 [FETCH INITIAL FIELDS] Buscando campos iniciais...');
        const response = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/pipeline-field`)
        if(response.data) {
          const allFields = response.data.data || [];
          console.log('🔍 [FETCH INITIAL FIELDS] Total de campos:', allFields.length);
          console.log('🔍 [FETCH INITIAL FIELDS] Campos integrados:', allFields.filter((f: any) => f.integrationConfig).length);
          
          const filtered = allFields.filter((f: any) => !f.integrationConfig);
          console.log('🟢 [FETCH INITIAL FIELDS] Campos personalizados (filtrados):', filtered.length);
          
          setFields(filtered)
        }
      } catch (error) {
        console.error('❌ [FETCH INITIAL FIELDS] Erro:', error)
      }
    }

    fetchFields()
  }, [])

  useEffect(() => {
    if (!isEditing && formData.field.id === 0 && formData.typeField.id === 0 && fieldTypes.length > 0) {
      const first = fieldTypes[0]
      setFormData((prev) => ({ ...prev, typeField: { id: first.id, name: first.name } }))
    }
  }, [fieldTypes, isEditing, formData.typeField.id, formData.field.id])

  useEffect(() => {
    if (editingField) {
      const typeFromList = fieldTypes.find((t) => t.id === (editingField.typeField?.id || editingField.typeFieldId))
      const fallbackTypeId = editingField.typeField?.id || editingField.typeFieldId || 0
      const fallbackTypeName = typeFromList?.name || editingField.typeField?.name || ''
      setFormData({
        field: { id: editingField.id, name: editingField.name },
        typeField: typeFromList || { id: fallbackTypeId, name: fallbackTypeName }
      })
      return
    }
    setFormData({ field: { id: 0, name: '' }, typeField: { id: 0, name: '' } })
  }, [editingField, fieldTypes])

  const handleSelectField = (
    field: Partial<{id: number, name: string}>,
    selectedTypeFromOption?: { id: number, name: string }
  ) => {
    const selectedType = selectedTypeFromOption || fields.find((f) => f.id === field.id)?.typeField
    setFormData(prev => ({
      ...prev,
      field: {id: field.id || 0, name: field.name || ''},
      typeField: selectedType || prev.typeField
    }))
  }

  const handleSaveFieldOnStep = async () => {
    try {
      const trimmedFieldName = formData.field.name.trim()
      const originalFieldId = Number(formData.field.id) || 0
      let pipelineFieldId = originalFieldId

      const isSameFieldAsEditing = isEditing && editingField && formData.field.id === editingField.id

      if (isSameFieldAsEditing && editingField) {
        const response = await axios.patch(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/pipeline-field/${editingField.id}`, {
          name: editingField.name,
          typeFieldId: formData.typeField.id || undefined
        })
        if (response.data) {
          onSaved?.(response.data.data)
          closeModal()
        }
        return
      }

      if (!isEditing && (!trimmedFieldName || !formData.typeField.id)) {
        return
      }

      if (!isEditing && originalFieldId <= 0) {
        const createResponse = await axios.post(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/pipeline-field`, {
          name: trimmedFieldName,
          typeFieldId: formData.typeField.id,
        })
        const created = createResponse.data?.data || createResponse.data
        if (!created?.id) {
          return
        }
        pipelineFieldId = created.id
        setFields((prev) => [{ id: created.id, name: created.name, typeField: created.typeField || formData.typeField }, ...prev])
      }

      if (originalFieldId > 0 && formData.typeField.id) {
        const selectedField = fields.find((f) => f.id === originalFieldId)
        const currentTypeId = selectedField?.typeField?.id || (selectedField as any)?.typeFieldId
        if (!currentTypeId || currentTypeId !== formData.typeField.id) {
          const patchResponse = await axios.patch(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/pipeline-field/${originalFieldId}`, {
            typeFieldId: formData.typeField.id
          })
          const updated = patchResponse.data?.data || patchResponse.data || {}
          const updatedType = updated.typeField || { id: formData.typeField.id, name: formData.typeField.name }
          setFields((prev) => prev.map((f) => f.id === originalFieldId ? { ...f, typeField: updatedType, typeFieldId: updatedType.id } : f))
        }
      }

      const response = await axios.post(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/step/${stepId}/field`, {
        pipelineFieldId,
        isRequired: isRequired,
        showOnCard: showOnCard
      })
      if(response.data) {
        onSaved?.()
        closeModal()
      }
    } catch (error) {
      console.error(error)
    }
  }
  
  return (
    <ModalTest>
      <S.ContainerOfModal width='700px'>
        <S.Header>
          <HeaderTitleWithMode>
            <S.HeaderTitle>{isEditing ? 'Editar campo' : 'Adicionar campo personalizado'}</S.HeaderTitle>
            {!isEditing && onToggleCustomMode && (
              <HeaderModeToggleWrapper>
                <HeaderModeLabel>Personalizado</HeaderModeLabel>
                <HeaderModeSwitch active onClick={() => onToggleCustomMode(false)}>
                  <HeaderModeThumb active />
                </HeaderModeSwitch>
              </HeaderModeToggleWrapper>
            )}
          </HeaderTitleWithMode>
          <S.CloseDiv onClick={() => closeModal()}>
            <X size={20} />
          </S.CloseDiv>
        </S.Header>
        <S.FormContainer>
          <InfiniteSelect
            label='Selecione o campo'
            placeholder='Digite aqui'
            emptyOptionsMessage='Nenhum dado encontrado'
            value={formData.field.id ? { id: formData.field.id, name: formData.field.name } : null}
            onSelect={(option) => {
              if (!option) {
                setFormData((prev) => ({ ...prev, field: { id: 0, name: '' } }))
                return
              }
              handleSelectField({ id: Number(option.id) || 0, name: option.name }, option.typeField)
              if (option.typeField) {
                setFormData((prev) => ({ ...prev, typeField: option.typeField }))
              }
            }}
            allowCreate
            allowDelete
            mode='crud'
            endpointConfig={{
              listPath: '/pipelines/pipeline-field',
              deletePath: '/pipelines/pipeline-field'
            }}
            fetchFunction={async (limit, offset, search) => {
              const response = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/pipeline-field`, {
                params: { search, limit, offset }
              })
              const raw = response.data?.data || []
              const filtered = raw.filter((f: any) => !f.integrationConfig);
              const normalized = filtered.map((item: any) => {
                const fallbackType = fieldTypes.find((t) => t.id === item.typeFieldId)
                return {
                  ...item,
                  typeField: item.typeField || (item.typeFieldId ? { id: item.typeFieldId, name: fallbackType?.name || '' } : fallbackType)
                }
              })
              setFields((prev) => {
                if (offset === 0) {
                  return normalized
                }
                const merged = [...prev, ...normalized]
                return Array.from(new Map(merged.map((item) => [item.id, item])).values())
              })
              return normalized
            }}
            onItemCreate={async (name) => {
              const normalizedName = name.trim()
              setFormData((prev) => ({ ...prev, field: { id: -1, name: normalizedName } }))
              return {
                id: -1,
                name: normalizedName,
                typeField: formData.typeField.id ? formData.typeField : undefined,
              }
            }}
            onItemDelete={async (id) => {
              if (Number(id) <= 0) {
                setFormData((prev) => prev.field.id === id
                  ? { ...prev, field: { id: 0, name: '' } }
                  : prev)
                return
              }
              await axios.delete(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/pipeline-field/${id}`)
              setFields((prev) => prev.filter((f) => f.id !== id))
              setFormData((prev) => prev.field.id === id
                ? { ...prev, field: { id: 0, name: '' } }
                : prev)
            }}
            getOptionLabel={(option) => option.name}
          />
          <S.InputDiv>
            <S.Label>Selecione o tipo</S.Label>
            <SelectStyled
              value={formData.typeField.id || ''}
              onChange={(e) => {
                const selectedId = Number(e.target.value) || 0
                const selectedType = fieldTypes.find((item) => item.id === selectedId)
                setFormData((prev) => ({
                  ...prev,
                  typeField: selectedType || { id: 0, name: '' },
                }))
              }}
            >
              <option value=''>Selecione</option>
              {fieldTypes.map((item) => (
                <option key={item.id} value={item.id}>{item.name}</option>
              ))}
            </SelectStyled>
          </S.InputDiv>
          <SwitchContainer>
            <SwitchTitle>Preenchimento obrigatório</SwitchTitle>
            <Switch active={isRequired} onClick={() => setIsRequired(!isRequired)}>
              <SwitchThumb active={isRequired}>
                {isRequired && (
                  <CheckIcon viewBox="0 0 24 24">
                    <polyline points="20 6 9 17 4 12" />
                  </CheckIcon>
                )}
              </SwitchThumb>
            </Switch>
          </SwitchContainer>
          <SwitchContainer>
            <SwitchTitle>Exibir na capa do card</SwitchTitle>
            <Switch active={showOnCard} onClick={() => setShowOnCard(!showOnCard)}>
              <SwitchThumb active={showOnCard}>
                {showOnCard && (
                  <CheckIcon viewBox="0 0 24 24">
                    <polyline points="20 6 9 17 4 12" />
                  </CheckIcon>
                )}
              </SwitchThumb>
            </Switch>
          </SwitchContainer>
        </S.FormContainer>
        <S.BottomDiv>
          <ButtonsRow>
            <CancelText onClick={() => closeModal()}>Cancelar</CancelText>
            {onBack && (
              <BackButton onClick={onBack}>
                <ArrowLeft size={16} /> Voltar
              </BackButton>
            )}
            <AddButton onClick={handleSaveFieldOnStep} disabled={!isEditing && (!formData.field.name.trim() || !formData.typeField.id)}>
              + {isEditing ? 'Alterar campo' : 'Adicionar campo'}
            </AddButton>
          </ButtonsRow>
        </S.BottomDiv>
      </S.ContainerOfModal>
    </ModalTest>
  )
}

const ButtonsRow = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.75rem;
`

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: center;
`

const CancelText = styled.span`
  color: #0090D3;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  padding: 0.7rem 1rem;

  &:hover {
    text-decoration: underline;
  }
`

const BackButton = styled.button`
  padding: 0.7rem 1.2rem;
  font-weight: 600;
  font-size: 14px;
  border-radius: 1.5rem;
  border: 1px solid #0090D3;
  color: #0090D3;
  background-color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s ease;

  &:hover {
    background-color: #f0f9ff;
  }
`

const AddButton = styled.button`
  background-color: #0090D3;
  color: #fff;
  padding: 0.7rem 1.2rem;
  font-weight: 600;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  border-radius: 1.5rem;
  cursor: pointer;
  border: none;
  outline: none;
  transition: all 0.2s ease;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    background-color: #007ab8;
  }
`

const SelectStyled = styled.select`
  width: 100%;
  border: none;
  outline: none;
  background-color: #EBEBEB;
  height: 2.98rem;
  padding: 0 1rem;
  border-radius: 2rem;
  font-size: 15px;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%23696969' stroke-width='2' stroke-linecap='round' stroke-linejoin='round' fill='none'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 1rem center;
`

const SwitchContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 1rem;
`

const SwitchTitle = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #1a1a1a;
`

const Switch = styled.div<{ active: boolean }>`
  width: 44px;
  height: 24px;
  border-radius: 12px;
  background-color: ${props => props.active ? '#0090D3' : '#E5E5E5'};
  cursor: pointer;
  position: relative;
  transition: all 0.2s ease;
`

const SwitchThumb = styled.div<{ active: boolean }>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: #fff;
  position: absolute;
  top: 2px;
  left: ${props => props.active ? '22px' : '2px'};
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
`

const CheckIcon = styled.svg`
  width: 12px;
  height: 12px;
  fill: none;
  stroke: #0090D3;
  stroke-width: 3;
  stroke-linecap: round;
  stroke-linejoin: round;
`

const HeaderTitleWithMode = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`

const HeaderModeToggleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const HeaderModeLabel = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #1a1a1a;
`

const HeaderModeSwitch = styled.div<{ active: boolean }>`
  width: 44px;
  height: 24px;
  border-radius: 12px;
  background-color: ${props => props.active ? '#0090D3' : '#E5E5E5'};
  cursor: pointer;
  position: relative;
  transition: all 0.2s ease;
`

const HeaderModeThumb = styled.div<{ active: boolean }>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: #fff;
  position: absolute;
  top: 2px;
  left: ${props => props.active ? '22px' : '2px'};
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
`

export default AddFieldOnStep